import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authzApi, type Permission, type Role } from '../../api/authz';

export function UserPermissionsPage() {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allPerms, setAllPerms] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void Promise.all([authzApi.myPermissions(), authzApi.listRoles(), authzApi.listPermissions()])
      .then(([me, r, p]) => {
        setPermissions(me.permissions.map(String));
        setRoles(r);
        setAllPerms(p);
      })
      .catch((err) => setError(String(err)));
  }, []);

  useEffect(() => {
    const role = roles.find((r) => r.id === selectedRole);
    setSelectedPerms(role?.permissions ?? []);
  }, [selectedRole, roles]);

  async function saveMatrix(e: FormEvent) {
    e.preventDefault();
    if (!selectedRole) return;
    try {
      await authzApi.setRolePermissions(selectedRole, selectedPerms);
      setRoles(await authzApi.listRoles());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  function togglePerm(key: string) {
    setSelectedPerms((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold text-slate-900">My permissions & matrix</h1>
      <nav className="mt-2 flex flex-wrap gap-3 text-sm">
        <Link to="/authorization/roles" className="text-blue-600 hover:underline">Roles</Link>
        <Link to="/authorization/permissions" className="text-blue-600 hover:underline">Permissions</Link>
        <Link to="/authorization/policies" className="text-blue-600 hover:underline">Policies</Link>
      </nav>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="font-medium">Your effective permissions</h2>
        <ul className="mt-3 flex flex-wrap gap-2">
          {permissions.map((p) => (
            <li key={p} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{p}</li>
          ))}
          {permissions.length === 0 && <li className="text-sm text-slate-600">No permissions (assign a role first).</li>}
        </ul>
      </section>

      <form onSubmit={saveMatrix} className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="font-medium">Permission matrix</h2>
        <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="mt-3 w-full max-w-md rounded-lg border border-slate-300 px-3 py-2">
          <option value="">Select role to edit…</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {selectedRole && (
          <ul className="mt-4 max-h-96 space-y-1 overflow-y-auto">
            {allPerms.slice(0, 80).map((p) => (
              <li key={p.key}>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={selectedPerms.includes(p.key)} onChange={() => togglePerm(p.key)} />
                  <span>{p.key}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <button type="submit" disabled={!selectedRole} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-50">Save role permissions</button>
      </form>
    </div>
  );
}
